import React, { useEffect, useState } from "react"
import styles from './header.module.css'
import { BsMeta } from "react-icons/bs"
import { FaRegUserCircle } from 'react-icons/fa'
import { FiHelpCircle, FiBell, FiBookOpen, FiLogOut, FiMoon, FiSun } from 'react-icons/fi'
import { MdOutlineNewspaper, MdCampaign, MdContentCopy, MdOutlineAddCard, MdOutlineCheck, MdOutlineClose, MdOutlineMailOutline, MdOutlineMenu, MdOutlinePlaylistAddCheck, MdOutlineTaskAlt, MdOutlineVpnKey, MdOutlineWhatsapp } from "react-icons/md"
import { TbApi } from "react-icons/tb"
import Logo from '../../../logo.svg'
import { useHeader } from "../../../contexts/headerContext"
import { useAuth } from "../../../contexts/authContext"
import { NavLink, useNavigate, useParams } from "react-router-dom"
import ImageWithFallback from "../../../components/ImageWithFallback"
import Button from "../../../components/Form/Button"
import Modal from "../../../components/Modal"
import api from "../../../services/api"
import { toast } from "react-toastify"
import { decideLeadAccessRequest } from "../../../schemas/Lead"

const supportWhatsAppUrl = "https://wa.me/554791737568"
const documentationUrl = "https://docs.oktto.com.br/"
const supportEmail = "contato@oktto.com.br"
const supportPhone = "+55 47 9173-7568"
const Header = ({ titleDefault = "", initialNotifications = [], showOptions = true, announcementUnreadCount = 0 }) => {
    const getInitialTheme = () => {
        const savedTheme = localStorage.getItem("@oktto:theme")

        if (["light", "dark"].includes(savedTheme)) {
            return savedTheme
        }

        return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light"
    }

    const [showPersonalOptions, setShowPersonalOptions] = useState()
    const [showNotifications, setShowNotifications] = useState(false)
    const [showAccessRequests, setShowAccessRequests] = useState(false)
    const [showSupport, setShowSupport] = useState(false)
    const [copiedSupportItem, setCopiedSupportItem] = useState("")
    const [theme, setTheme] = useState(getInitialTheme)
    const [notificationActionLoading, setNotificationActionLoading] = useState("")
    const [dismissedPopupNotifications, setDismissedPopupNotifications] = useState([])
    const [reviewAccessNotification, setReviewAccessNotification] = useState(null)
    const { account_id } = useParams()
    const { title, setTitle, notifications, addNewNotification, removeNotification, handleToogleShowMenu } = useHeader()
    const { account, user, permissions, showAllOptions, handleLogout } = useAuth()

    const navigate = useNavigate()

    useEffect(() => {
        if (titleDefault) {
            setTitle(titleDefault)
        }
    }, [titleDefault])

    useEffect(() => {
        if (initialNotifications.length > 0) {
            initialNotifications?.forEach(notification => {
                addNewNotification(notification.id, notification.type, notification.message, notification.data, notification.onClick, { silentPopup: true })
            })
        }
    }, [initialNotifications])

    const handleToogleShowPersonalOptions = (e) => {
        e.stopPropagation()
        setShowPersonalOptions(previousValue => !previousValue)
    }

    const handleToogleShowNotifications = (e) => {
        e.stopPropagation()
        setShowNotifications(previousValue => !previousValue)
        setShowAccessRequests(false)
    }

    const handleToogleShowAccessRequests = (e) => {
        e.stopPropagation()
        setShowAccessRequests(previousValue => !previousValue)
        setShowNotifications(false)
    }

    const handleShowSupport = (e) => {
        e.stopPropagation()
        setShowSupport(true)
    }

    const handleHideSupport = (e) => {
        e.stopPropagation()
        setShowSupport(false)
    }

    const handleToggleTheme = (e) => {
        e.stopPropagation()

        setTheme(previousTheme => {
            const nextTheme = previousTheme === "dark" ? "light" : "dark"

            localStorage.setItem("@oktto:theme", nextTheme)
            document.documentElement.dataset.theme = nextTheme

            return nextTheme
        })
    }

    const getIconNotificationByType = (type) => {
        return type === "meta_ads" ? <BsMeta className="icon-md" /> : (type === "api" ? <TbApi className="icon-md" /> : (type === "credits_recharge" ? <MdOutlineAddCard className="icon-md" /> : (type === "task_schedule" ? <MdOutlineTaskAlt className="icon-md" /> : (type === "duplicate_queue" ? <MdOutlinePlaylistAddCheck className="icon-md" /> : <></>))))
    }

    const handleClickNotification = (notification) => {
        if (notification.type === "api") {
            navigate(`/${account_id}/integrations/forms`)
        } else if (notification.type === "credits_recharge") {
            navigate(`/${account_id}/myaccount`)
        } else if (notification.type === "lead_access_request_resolved" && notification?.data?.status === "approved") {
            navigate(`/${account_id}/kanban?openModal=1&leadId=${notification?.data?.lead_id}`)
            removeNotification(notification.id)
        } else if (notification.type === "chat_access_request_resolved" && notification?.data?.status === "approved") {
            navigate(`/${account_id}/chat/${notification?.data?.conversation_id}`)
            removeNotification(notification.id)
        } else {
            notification?.onClick()
        }
    }

    const handleOpenAccessTarget = (notification) => {
        if (notification.type?.startsWith("chat")) {
            const conversationId = notification?.data?.conversation_id || notification?.data?.conversation?._id
            if (conversationId) {
                navigate(`/${account_id}/chat/${conversationId}`)
                closeAccessReview()
            }
        } else if (notification.type?.startsWith("lead")) {
            const leadId = notification?.data?.lead_id
            if (leadId) {
                navigate(`/${account_id}/kanban?openModal=1&leadId=${leadId}`)
                closeAccessReview()
            }
        }
    }

    const dismissPopupNotification = (notificationId) => {
        setDismissedPopupNotifications(previousValue => {
            if (previousValue.includes(notificationId)) return previousValue
            return [...previousValue, notificationId]
        })
    }

    const openAccessReview = (notification) => {
        setReviewAccessNotification(notification)
        dismissPopupNotification(notification.id)
        setShowAccessRequests(false)
    }

    const closeAccessReview = () => {
        setReviewAccessNotification(null)
    }

    const decideAccessRequest = async (notification, action) => {
        const requestId = notification?.data?.request_id || notification?.id
        if (!requestId || notificationActionLoading) return

        try {
            setNotificationActionLoading(`${requestId}-${action}`)
            if (notification.type === "lead_access_request") {
                await decideLeadAccessRequest(requestId, { account_id, action })
            } else if (notification.type === "chat_access_request") {
                const response = await api.patch(`/chat/conversations/access-requests/${requestId}`, { account_id, action })
                toast.success(response.data?.message)
            }
            removeNotification(notification.id)
            dismissPopupNotification(notification.id)
            closeAccessReview()
        } catch (err) {
            if (notification.type !== "lead_access_request") {
                toast.error(err?.response?.data?.message || "Não foi possível processar a solicitação.")
            }
        } finally {
            setNotificationActionLoading("")
        }
    }

    const renderNotificationActions = (notification, mode = "list") => {
        if (notification.type === "lead_access_request_pending" || notification.type === "chat_access_request_pending") {
            return null
        }

        if (notification.type === "lead_access_request" || notification.type === "chat_access_request") {
            const requestId = notification?.data?.request_id || notification?.id
            const approving = notificationActionLoading === `${requestId}-approve`
            const denying = notificationActionLoading === `${requestId}-deny`
            const disabled = Boolean(notificationActionLoading)

            if (mode !== "review") {
                return (
                    <div className={styles.notificationActions}>
                        <Button variant="secondary" type="button" onClick={(e) => { e.stopPropagation(); openAccessReview(notification) }}>
                            <MdOutlinePlaylistAddCheck /> Revisar
                        </Button>
                    </div>
                )
            }

            return (
                <div className={`${styles.notificationActions} ${styles.reviewNotificationActions}`}>
                    <Button variant="neutral" className={`${styles.accessReviewOpenTarget}`} type="button" onClick={(e) => { e.stopPropagation(); handleOpenAccessTarget(notification) }}>
                        {notification.type === "chat_access_request" ? <MdOutlineWhatsapp /> : <MdOutlineTaskAlt />} Ver {notification.type === "chat_access_request" ? "conversa" : "lead"}
                    </Button>
                    <div className={styles.accessDecisionActions}>
                        <Button variant="primary" type="button" disabled={disabled} onClick={(e) => { e.stopPropagation(); decideAccessRequest(notification, "approve") }}>
                            <MdOutlineCheck /> {approving ? "Liberando..." : "Liberar acesso"}
                        </Button>
                        <Button variant="secondary" type="button" disabled={disabled} onClick={(e) => { e.stopPropagation(); decideAccessRequest(notification, "deny") }}>
                            <MdOutlineClose /> {denying ? "Negando..." : "Negar"}
                        </Button>
                    </div>
                </div>
            )
        }

        if (notification.type === "lead_access_request_resolved" && notification?.data?.status === "approved") {
            return (
                <Button onClick={() => handleClickNotification(notification)}>
                    <MdOutlineTaskAlt /> Abrir lead
                </Button>
            )
        }

        if (notification.type === "chat_access_request_resolved" && notification?.data?.status === "approved") {
            return (
                <Button onClick={() => handleClickNotification(notification)}>
                    <MdOutlineWhatsapp /> Abrir conversa
                </Button>
            )
        }

        if (notification.type === "meta_ads" || notification.type === "api" || notification.type === "credits_recharge" || notification.type === "task_schedule" || notification.type === "duplicate_queue") {
            return (
                <Button onClick={() => handleClickNotification(notification)}>
                    {getIconNotificationByType(notification.type)}
                    {notification.type === "credits_recharge" ? "Realizar Recarga" : notification.type === "task_schedule" ? "Visualizar" : notification.type === "duplicate_queue" ? "Revisar" : "Corrigir"}
                </Button>
            )
        }

        return null
    }

    const accessNotificationTypes = [
        "lead_access_request",
        "lead_access_request_pending",
        "lead_access_request_resolved",
        "chat_access_request",
        "chat_access_request_pending",
        "chat_access_request_resolved",
    ]

    const accessNotifications = notifications.filter(notification => accessNotificationTypes.includes(notification.type))
    const accessApprovalCount = accessNotifications.filter(notification => ["lead_access_request", "chat_access_request"].includes(notification.type)).length
    const regularNotifications = notifications.filter(notification => !accessNotificationTypes.includes(notification.type))

    const popupNotifications = accessNotifications
        .filter(notification => !notification.silentPopup && !["lead_access_request_pending", "chat_access_request_pending"].includes(notification.type) && !dismissedPopupNotifications.includes(notification.id))
        .slice(-3)

    const getPopupTitleByNotification = (notification) => {
        if (notification.type === "lead_access_request") return "Solicitação de acesso ao lead"
        if (notification.type === "lead_access_request_pending") return "Aguardando aprovação do lead"
        if (notification.type === "chat_access_request") return "Solicitação de acesso ao chat"
        if (notification.type === "chat_access_request_pending") return "Aguardando aprovação do chat"
        if (notification.type === "lead_access_request_resolved") return notification?.data?.status === "approved" ? "Acesso ao lead liberado" : "Acesso ao lead negado"
        if (notification.type === "chat_access_request_resolved") return notification?.data?.status === "approved" ? "Acesso ao chat liberado" : "Acesso ao chat negado"
        return "Notificação"
    }

    const getPopupIconByNotification = (notification) => {
        if (notification.type?.startsWith("chat")) return <MdOutlineWhatsapp />
        return <MdOutlineTaskAlt />
    }

    const getConversationContactLabel = (conversation = {}) => {
        const name = String(conversation?.contact_name || "").trim()
        const identifier = String(conversation?.contact_identifier || "").trim()

        if (name && name !== "Contato sem nome") return name
        return identifier || name || "Contato sem nome"
    }

    const getConversationStatusLabel = (status) => {
        const labels = {
            waiting: "Aguardando",
            opened: "Em aberto",
            finalized: "Finalizada",
        }

        return labels[status] || status || "-"
    }

    const renderAccessDetails = (notification) => {
        const conversation = notification?.data?.conversation
        if (!conversation) return null

        const identifier = conversation?.contact_identifier
            ? `${conversation.contact_identifier_label || "Identificador"}: ${conversation.contact_identifier}`
            : ""

        return (
            <div className={styles.accessDetails}>
                <span><strong>Contato:</strong> {getConversationContactLabel(conversation)}</span>
                {identifier && <span><strong>{identifier}</strong></span>}
                {conversation.inbox_name && <span><strong>Caixa:</strong> {conversation.inbox_name}</span>}
                {conversation.status && <span><strong>Status:</strong> {getConversationStatusLabel(conversation.status)}</span>}
            </div>
        )
    }

    const renderLeadAccessDetails = (notification) => {
        const lead = notification?.data?.lead || {}
        const contacts = Array.isArray(lead.contacts) ? lead.contacts : []
        const owners = Array.isArray(lead.owners) ? lead.owners : []

        return (
            <div className={styles.accessLeadDetails}>
                <div className={styles.accessReviewSectionTitle}>
                    <strong>{lead.name || notification?.data?.lead_name || "Lead"}</strong>
                    <span>{lead.discarted ? "Descartado" : lead.status || "Em atendimento"}</span>
                </div>
                <div className={styles.accessReviewGrid}>
                    <div>
                        <span>Funil</span>
                        <strong>{lead.funnel_name || "-"}</strong>
                    </div>
                    <div>
                        <span>Etapa</span>
                        <strong>{lead.stage_name || "-"}</strong>
                    </div>
                    <div>
                        <span>Equipe</span>
                        <strong>{lead.team_name || "-"}</strong>
                    </div>
                </div>
                <div className={styles.accessLeadList}>
                    <span>Contatos</span>
                    {contacts.length > 0
                        ? contacts.map((contact, index) => (
                            <strong key={contact._id || `${contact.phone}-${contact.email}-${index}`}>
                                {[contact.name, contact.phone, contact.email].filter(Boolean).join(" · ")}
                            </strong>
                        ))
                        : <strong>-</strong>
                    }
                </div>
                <div className={styles.accessLeadList}>
                    <span>Responsáveis</span>
                    {owners.length > 0
                        ? <strong>{owners.map(owner => owner.name || owner.email).filter(Boolean).join(", ")}</strong>
                        : <strong>-</strong>
                    }
                </div>
            </div>
        )
    }

    const formatAccessMessageDate = (date) => {
        if (!date) return ""

        return new Date(date).toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        })
    }

    const renderAccessReviewModal = () => {
        if (!reviewAccessNotification) return null

        const notification = reviewAccessNotification
        const conversation = notification?.data?.conversation || {}
        const isChatRequest = notification.type === "chat_access_request" || notification.type === "chat_access_request_pending"
        const isPendingRequest = notification.type === "lead_access_request" || notification.type === "chat_access_request"
        const isSentPendingRequest = notification.type === "lead_access_request_pending" || notification.type === "chat_access_request_pending"
        const contactLabel = getConversationContactLabel(conversation)
        const latestMessages = Array.isArray(conversation?.latest_messages) ? conversation.latest_messages : []

        return (
            <Modal
                icon={<MdOutlineVpnKey />}
                title={getPopupTitleByNotification(notification)}
                description={notification.message}
                closeModal={closeAccessReview}
                heightFitContent
            >
                <div className={styles.accessReview}>
                    {isChatRequest &&
                        <>
                            <div className={styles.accessReviewContact}>
                                <div className={styles.accessReviewAvatar}>
                                    {conversation.contact_picture ?
                                        <ImageWithFallback
                                            src={conversation.contact_picture}
                                            alt={contactLabel}
                                            functionError={() => <FaRegUserCircle />}
                                        />
                                        :
                                        <FaRegUserCircle />
                                    }
                                </div>
                                <div className={styles.accessReviewContactText}>
                                    <strong>{contactLabel}</strong>
                                    {conversation.contact_identifier && <span>{conversation.contact_identifier_label || "Identificador"}: {conversation.contact_identifier}</span>}
                                    {conversation.inbox_name && <span>Caixa: {conversation.inbox_name}</span>}
                                </div>
                            </div>

                            <div className={styles.accessReviewGrid}>
                                <div>
                                    <span>Solicitante</span>
                                    <strong>{notification?.data?.requester_name || "Usuário"}</strong>
                                </div>
                                <div>
                                    <span>Status da conversa</span>
                                    <strong>{getConversationStatusLabel(conversation.status)}</strong>
                                </div>
                                <div>
                                    <span>Canal</span>
                                    <strong>{conversation.platform || "WhatsApp"}</strong>
                                </div>
                            </div>

                            <div className={styles.accessReviewPreview}>
                                <div className={styles.accessReviewSectionTitle}>
                                    <strong>Prévia da conversa</strong>
                                    {latestMessages.length > 0 && <span>Últimas mensagens</span>}
                                </div>
                                {latestMessages.length > 0 ?
                                    latestMessages.map(message => (
                                        <div className={styles.accessReviewMessage} key={message._id || `${message.created_at}-${message.text}`}>
                                            <div>
                                                <strong>{message.direction === "send" ? "Atendente" : contactLabel}</strong>
                                                <span>{formatAccessMessageDate(message.created_at)}</span>
                                            </div>
                                            <p>{message.text}</p>
                                        </div>
                                    ))
                                    :
                                    <div className={styles.accessReviewEmpty}>Ainda não há mensagens recentes para exibir.</div>
                                }
                            </div>
                        </>
                    }

                    {!isChatRequest &&
                        <>
                            {renderLeadAccessDetails(notification)}
                            <div className={styles.accessReviewEmpty}>
                                {isSentPendingRequest
                                    ? "Você já enviou esta solicitação. Assim que o responsável responder, você receberá uma notificação."
                                    : `${notification?.data?.requester_name || "Um usuário"} quer acessar este lead.`
                                }
                            </div>
                        </>
                    }

                    {isSentPendingRequest &&
                        <div className={styles.accessReviewEmpty}>
                            Solicitação enviada. Aguardando aprovação do responsável.
                        </div>
                    }

                    {isPendingRequest &&
                        <div className={styles.accessReviewActions}>
                            {renderNotificationActions(notification, "review")}
                        </div>
                    }
                </div>
            </Modal>
        )
    }

    const handleCopySupportValue = async (e, value, item) => {
        e.stopPropagation()
        e.preventDefault()

        try {
            if (!navigator.clipboard?.writeText) {
                throw new Error("Clipboard API indisponivel")
            }

            await navigator.clipboard.writeText(value)
        } catch (error) {
            const textarea = document.createElement("textarea")
            textarea.value = value
            textarea.setAttribute("readonly", "")
            textarea.style.position = "absolute"
            textarea.style.left = "-9999px"
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand("copy")
            document.body.removeChild(textarea)
        }

        setCopiedSupportItem(item)
        setTimeout(() => setCopiedSupportItem(""), 1500)
    }

    const renderSupportLink = ({ item, href, icon, text, copyValue }) => (
        <div className={styles.supportOption}>
            <a className={styles.supportLink} href={href} target={href.startsWith("mailto:") ? undefined : "_blank"} rel={href.startsWith("mailto:") ? undefined : "noreferrer"}>
                {icon}
                <span>{text}</span>
            </a>
            {copyValue &&
                <button
                    type="button"
                    className={styles.copySupport}
                    onClick={(e) => handleCopySupportValue(e, copyValue, item)}
                    aria-label={`Copiar ${text}`}
                    title={`Copiar ${text}`}
                >
                    {copiedSupportItem === item ? <MdOutlineCheck /> : <MdContentCopy />}
                </button>
            }
        </div>
    )

    return (
        <>
            <header className={styles.header}>
                <div className={styles.info}>
                    <div className={styles.hamburguerMenu} onClick={handleToogleShowMenu}>
                        <MdOutlineMenu />
                    </div>
                    <img src={Logo} alt="Logo Oktto" />
                    <span className={styles.title}>{title}</span>
                </div>
                <div className={styles.actions}>
                    {showOptions ?
                        <>
                            <NavLink
                                className={({ isActive }) => `${styles.headerShortcut} ${isActive ? styles.headerShortcutActive : ""}`}
                                to={`/${account_id}/announcements`}
                                title="Comunicados"
                                aria-label={announcementUnreadCount > 0 ? `Comunicados: ${announcementUnreadCount} não lidos` : "Comunicados"}
                            >
                                <MdCampaign />
                                {announcementUnreadCount > 0 && <span className={styles.shortcutBadge}>{announcementUnreadCount > 99 ? "99+" : announcementUnreadCount}</span>}
                            </NavLink>
                            <NavLink
                                className={({ isActive }) => `${styles.headerShortcut} ${isActive ? styles.headerShortcutActive : ""}`}
                                to={`/${account_id}/platform-updates`}
                                title="Novidades"
                                aria-label="Novidades"
                            >
                                <MdOutlineNewspaper />
                            </NavLink>
                            <div className={styles.notifications} onClick={handleToogleShowNotifications}>
                                <FiBell />
                                {regularNotifications.length > 0 && <span className={styles.number}>{regularNotifications.length}</span>}
                                {showNotifications &&
                                    <>
                                        <div className={styles.body}>
                                            {regularNotifications.length > 0 && regularNotifications.map(notification => (
                                                <div className={styles.notification} key={notification.id}>
                                                    <span>{notification.message}</span>
                                                    {renderNotificationActions(notification)}
                                                </div>
                                            ))}
                                            {regularNotifications.length === 0 && <div className={styles.notification}><span>Nenhuma notificação no momento!</span></div>}
                                        </div>
                                        <div className={styles.background} />
                                    </>
                                }
                            </div>
                            <div className={`${styles.notifications} ${styles.accessRequestsButton} ${accessApprovalCount > 0 ? styles.accessRequestsButtonAttention : ""}`} onClick={handleToogleShowAccessRequests} title="Solicitações de acesso">
                                <MdOutlineVpnKey />
                                {accessApprovalCount > 0 && <span className={styles.number}>{accessApprovalCount}</span>}
                                {showAccessRequests &&
                                    <>
                                        <div className={`${styles.body} ${styles.accessRequestsBody}`}>
                                            {accessNotifications.length > 0 && accessNotifications.map(notification => (
                                                <div className={styles.notification} key={notification.id}>
                                                    <strong className={styles.notificationTitle}>{getPopupTitleByNotification(notification)}</strong>
                                                    <span>{notification.message}</span>
                                                    {renderAccessDetails(notification)}
                                                    {renderNotificationActions(notification)}
                                                </div>
                                            ))}
                                            {accessNotifications.length === 0 && <div className={styles.notification}><span>Nenhuma solicitação de acesso no momento!</span></div>}
                                        </div>
                                        <div className={styles.background} />
                                    </>
                                }
                            </div>
                            <button
                                type="button"
                                className={styles.themeToggle}
                                onClick={handleToggleTheme}
                                aria-label={theme === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}
                                title={theme === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}
                            >
                                {theme === "dark" ? <FiSun /> : <FiMoon />}
                            </button>
                            <div className={`${styles.notifications} ${styles.support}`} onClick={handleShowSupport}>
                                <FiHelpCircle />
                                {showSupport &&
                                    <>
                                        <div className={`${styles.body} ${styles.supportBody}`}>
                                            <div className={styles.supportItem}>
                                                {renderSupportLink({
                                                    item: "email",
                                                    href: `mailto:${supportEmail}`,
                                                    icon: <MdOutlineMailOutline />,
                                                    text: supportEmail,
                                                    copyValue: supportEmail,
                                                })}
                                            </div>
                                            <div className={styles.supportItem}>
                                                {renderSupportLink({
                                                    item: "whatsapp",
                                                    href: supportWhatsAppUrl,
                                                    icon: <MdOutlineWhatsapp />,
                                                    text: supportPhone,
                                                    copyValue: supportPhone,
                                                })}
                                            </div>
                                            <div className={styles.supportItem}>
                                                {renderSupportLink({
                                                    item: "documentation",
                                                    href: documentationUrl,
                                                    icon: <FiBookOpen />,
                                                    text: "Documentação",
                                                })}
                                            </div>
                                        </div>
                                        <div className={styles.background} onClick={handleHideSupport} />
                                    </>
                                }
                            </div>
                            <div className={styles.option} onClick={handleToogleShowPersonalOptions}>
                                <div className={styles.groupData}>
                                    <div className={styles.userData}>
                                        <span>{user.name || ''}</span>
                                        <span>{account?.name || ''}</span>
                                    </div>
                                    {user?.image ?
                                        <ImageWithFallback
                                            src={user.image}
                                            alt="Imagem de Perfil"
                                            functionError={() => <FaRegUserCircle />}
                                        />
                                        :
                                        <FaRegUserCircle />
                                    }
                                </div>
                                {showPersonalOptions &&
                                    <>
                                        <ul>
                                            <li><NavLink to={`/${account_id}/profile`}>Perfil</NavLink></li>
                                            {permissions?.finance >= 1 && <li><NavLink to={`/${account_id}/myaccount`}>Minha Conta</NavLink></li>}
                                            {(showAllOptions && permissions?.import >= 1) && <li><NavLink to={`/${account_id}/import`}>Importar Dados</NavLink></li>}
                                            {permissions?.reports >= 1 && <li><NavLink to={`/${account_id}/reports`}>Relatórios</NavLink></li>}
                                            <li className={styles.mobileProfileShortcut}>
                                                <NavLink to={`/${account_id}/announcements`}>
                                                    <span>Comunicados</span>
                                                    {announcementUnreadCount > 0 && <span className={styles.menuBadge}>{announcementUnreadCount > 99 ? "99+" : announcementUnreadCount}</span>}
                                                </NavLink>
                                            </li>
                                            <li className={styles.mobileProfileShortcut}>
                                                <NavLink to={`/${account_id}/platform-updates`}>Novidades</NavLink>
                                            </li>
                                            {(showAllOptions && permissions?.settings >= 1) && <li><NavLink to={`/${account_id}/settings`}>Configurações</NavLink></li>}
                                            {(showAllOptions && permissions?.integrations >= 1) && <li><NavLink to={`/${account_id}/integrations`}>Integrações</NavLink></li>}
                                            {(showAllOptions && permissions?.settings >= 1) && <li><NavLink to={`/${account_id}/trashs`}>Lixeira</NavLink></li>}
                                            <li><NavLink to={"/"}>Acessar Outra Conta</NavLink></li>
                                            <li onClick={handleLogout}>Sair</li>
                                        </ul>
                                        <div className={styles.background} />
                                    </>
                                }
                            </div>
                        </>
                        :
                        <FiLogOut onClick={handleLogout} />
                    }
                </div>
            </header>

            {popupNotifications.length > 0 &&
                <div className={styles.popupStack}>
                    {popupNotifications.map(notification => (
                        <div className={styles.accessPopup} key={notification.id}>
                            <div className={styles.accessPopupIcon}>
                                {getPopupIconByNotification(notification)}
                            </div>
                            <div className={styles.accessPopupContent}>
                                <div className={styles.accessPopupHeader}>
                                    <strong>{getPopupTitleByNotification(notification)}</strong>
                                    <button
                                        type="button"
                                        className={styles.accessPopupClose}
                                        onClick={() => dismissPopupNotification(notification.id)}
                                        aria-label="Fechar notificação"
                                        title="Fechar"
                                    >
                                        <MdOutlineClose />
                                    </button>
                                </div>
                                <span>{notification.message}</span>
                                {renderAccessDetails(notification)}
                                {renderNotificationActions(notification)}
                            </div>
                        </div>
                    ))}
                </div>
            }
            {renderAccessReviewModal()}
        </>
    )
}

export default Header
